import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AddEvent, CancelEvent, EditEvent, GridComponent, RemoveEvent, SaveEvent } from '@progress/kendo-angular-grid';
import { ODataService } from 'src/app/dashboard/services/odata.service';
import { ToasterService } from 'src/app/shared/notification-toast/toaster.service';
import { AppSettings } from 'src/assets/config/app-settings';
import { Number } from 'src/app/core/models/customer.model';
import { SessionService } from 'src/app/core/services/session.service';
import { EventsService } from 'src/app/core/services/events.service';
import { tap } from 'rxjs';

// Per-step outcome of a deactivation (mirrors the backend `results` object).
export interface DeactivateResults {
  telnyx: { ok: boolean; error?: string; alreadyReleased?: boolean };
  translation: { ok: boolean; error?: string; deleted?: number };
  extension: { ok: boolean; error?: string; deleted?: number };
  mysql: { ok: boolean; error?: string; assignmentsDeleted?: number; phoneNumbersDeleted?: number };
  files: { ok: boolean; error?: string; attempted?: number; failed?: number };
  customerPull: { ok: boolean; error?: string };
  mongo: { ok: boolean; error?: string };
}

// Per-step outcome of a number change (mirrors the backend `results` object).
// `asteriskProvision` only appears when there were no existing rows to repoint and the routing
// rows had to be created fresh via the queue API.
export interface ChangeNumberResults {
  asterisk: {
    ok: boolean;
    error?: string;
    oldKey?: string;
    newKey?: string;
    translationsUpdated?: number;
    extensionsUpdated?: number;
  };
  asteriskProvision?: { ok: boolean; error?: string; reason?: string };
  mongo: { ok: boolean; error?: string; touched?: string[] };
}

@Injectable({
  providedIn: 'root',
})
export class NumberGridService extends ODataService {
  private editedRowIndex: number | undefined;
  public formGroup: FormGroup | undefined;

  constructor(
    http: HttpClient,
    private toaster: ToasterService,
    private settings: AppSettings,
    private session: SessionService,
    private events: EventsService
  ) {
    const url = `${settings.apiBaseUrl}/api/numbers/odata`;
    super(http, url);

    this.state = {
      skip: 0,
      take: 10,
      sort: [{ field: 'createdAt', dir: 'desc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };

    this.mapper = (data: any[]) => {
      data.map((x) => {
        x.createdAt = new Date(x.createdAt);
      });
      return data;
    };
  }

  public addHandler(args: AddEvent): void {
    this.closeEditor(args.sender);
    // define all editable fields validators and default values
    // this.formGroup = new FormGroup({
    //   ProductID: new FormControl(),
    //   ProductName: new FormControl("", Validators.required),
    //   UnitPrice: new FormControl(0),
    //   UnitsInStock: new FormControl(
    //     "",
    //     Validators.compose([
    //       Validators.required,
    //       Validators.pattern("^[0-9]{1,3}"),
    //     ])
    //   ),
    //   Discontinued: new FormControl(false),
    // });
    // show the new row editor, with the `FormGroup` build above
    args.sender.addRow(this.formGroup);
  }

  public editHandler(args: EditEvent): void {
    // define all editable fields validators and default values
    const { dataItem } = args;
    this.closeEditor(args.sender);

    this.formGroup = new FormGroup({
      _id: new FormControl(dataItem._id),
      name: new FormControl(dataItem.name, Validators.required),
    });

    this.editedRowIndex = args.rowIndex;
    // put the row in edit mode, with the `FormGroup` build above
    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  public cancelHandler(args: CancelEvent): void {
    // close the editor for the given row
    this.closeEditor(args.sender, args.rowIndex);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    const number = formGroup.value;
    this.patchNumber(number).subscribe((response) => {
      this.read(() => {
        const index = this.session.user!.customer!.numbers?.findIndex((x) => x._id === response.number!._id);
        this.session.user!.customer!.numbers![index!] = response.number!;
        if(this.session.user!.selectedNumber?._id === response.number!._id) {
          this.session.user!.selectedNumber = response.number!;
        }
        this.formGroup = undefined;
        sender.closeRow(rowIndex);
      });
    });
  }

  private patchNumber(number: Number) {
    const url = `${this.settings.apiBaseUrl}/api/numbers/patch`;
    return this.http.patch<{ number: Number }>(url, number);
  }

  // Persist a single number's editable fields (name) and keep the cached session numbers in sync.
  public save(number: Number) {
    return this.patchNumber(number).pipe(
      tap((response) => {
        const numbers = this.session.user?.customer?.numbers;
        if (numbers) {
          const index = numbers.findIndex((x) => x._id === response.number!._id);
          if (index >= 0) numbers[index] = response.number!;
        }
        if (this.session.user?.selectedNumber?._id === response.number!._id) {
          this.session.user!.selectedNumber = response.number!;
        }
      })
    );
  }

  // Toggle call forwarding for a single line and keep the cached session numbers in sync, so the
  // header reflects the saved state when switching between lines.
  public updateIvrStatus(numberId: string, forward: boolean, forwardNumber: string | null) {
    const url = `${this.settings.apiBaseUrl}/api/numbers/ivrstatus`;
    return this.http.put<{ number: Number }>(url, { _id: numberId, forward, forwardNumber }).pipe(
      tap(({ number: saved }) => {
        const apply = (n?: Number) => {
          if (n && n._id === saved._id) {
            n.forward = saved.forward;
            n.forwardNumber = saved.forwardNumber;
          }
        };
        this.session.user?.customer?.numbers?.forEach(apply);
        apply(this.session.user?.selectedNumber);
        this.events.forwardingChange(saved._id!);
      })
    );
  }

  // Super-admin only: repoint an existing line at a different phone number. Rewrites the Mongo
  // document (including its in-document denormalized copies) and updates the Asterisk translation
  // and inbound dialplan rows in place. Makes no Telnyx call — routing changes, ownership does not.
  //
  // Deliberately does NOT patch the cached session here (unlike save() above): several
  // root-provided grid services capture selectedNumber.number in their constructors, so the caller
  // does a full page reload instead.
  public changeNumber(numberId: string, newNumber: string) {
    const url = `${this.settings.apiBaseUrl}/api/numbers/change-number`;
    return this.http.patch<{
      message: string;
      oldPhone: string;
      newPhone: string;
      number: Number;
      results: ChangeNumberResults;
    }>(url, { numberId, newNumber });
  }

  public updateDefaultNumber(number: Number) {
    const request = {
      _id: number._id,
      customerId: number.customer,
    };
    const url = `${this.settings.apiBaseUrl}/api/numbers/updateDefault`;
    return this.http.patch<{ numbers: Number[] }>(url, request)
      .pipe(tap((response) => {
        this.session.user!.customer!.numbers = response.numbers;
        const defaultNumber = response.numbers.find((number) => number.isDefault);        
        this.session.user!.selectedNumber = defaultNumber;
        this.events.defaultNumberChange(defaultNumber?._id!);
      }));
  }

  // Deactivate a number: releases it from Telnyx, cleans up orphaned data and deletes
  // the document server-side. Keeps the cached session numbers in sync.
  public deactivate(numberId: string) {
    const url = `${this.settings.apiBaseUrl}/api/numbers/deactivate?numberId=${numberId}`;
    return this.http.delete<{ message: string; results: DeactivateResults }>(url).pipe(
      tap(() => {
        const numbers = this.session.user?.customer?.numbers;
        if (numbers) {
          const index = numbers.findIndex((x) => x._id === numberId);
          if (index >= 0) numbers.splice(index, 1);
        }
        if (this.session.user?.selectedNumber?._id === numberId) {
          this.session.user!.selectedNumber = undefined;
        }
      })
    );
  }

  public removeHandler(args: RemoveEvent): void {
    // remove the current dataItem from the current data source,
    // `editService` in this example
    // this.editService.remove(args.dataItem);
  }

  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    // close the editor
    grid.closeRow(rowIndex);
    // reset the helpers
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
